/**
 * Drops keys whose value is `undefined`.
 *
 * The repo compiles with `exactOptionalPropertyTypes`, which draws a real
 * distinction between "this property is absent" and "this property is present
 * and set to undefined". Radix's prop types are written the strict way
 * (`value?: string`, not `value?: string | undefined`), so passing
 * `value={undefined}` down from a component whose own state is
 * `string | undefined` is a type error rather than the no-op everyone expects.
 *
 * Spreading `omitUndefined({ value, defaultValue })` onto the primitive fixes
 * it honestly: an unset prop is genuinely not passed, which is also what makes
 * Radix's controlled/uncontrolled detection work. The alternative — widening
 * every Radix prop with a cast — would silently turn "uncontrolled" into
 * "controlled with an undefined value" and break the component at runtime.
 */
export const omitUndefined = <T extends Record<string, unknown>>(
  props: T,
): { [K in keyof T]: Exclude<T[K], undefined> } => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) result[key] = value;
  }
  return result as { [K in keyof T]: Exclude<T[K], undefined> };
};
